import { useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Plus, Trash2, Camera, MapPin, Search, ChevronRight, ChevronLeft, CheckCircle2, FileText, Save } from 'lucide-react';
import { clientesApi, empleadosApi, serviciosApi, refaccionesApi, relacionesApi } from '../api/endpoints';
import { Spinner } from '../components/ui/Spinner';
import { Alert } from '../components/ui/Alert';

export function NuevaRelacion() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSuggestion = location.state?.suggestion;
  const initialClienteId = location.state?.clienteId;

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [clientes, setClientes] = useState([]);
  const [form, setForm] = useState({
    clienteId: initialClienteId || null,
    fechaInicio: initialSuggestion?.fechaInicio || new Date().toISOString().slice(0, 10),
    fechaFin: initialSuggestion?.fechaFin || new Date().toISOString().slice(0, 10),
    descripcionGeneral: 'A continuación, se detalla la relación de servicios de <b>Soporte Técnico, Mantenimiento y Gestión de Plataformas</b> realizados durante el periodo solicitado.',
    estatus: 'Borrador',
    items: [],
  });

  const [empleados, setEmpleados] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [refacciones, setRefacciones] = useState([]);

  useEffect(() => {
    (async () => {
      const cs = await clientesApi.list();
      setClientes(cs);
      if (!form.clienteId && cs.length > 0) setForm(f => ({ ...f, clienteId: cs[0].id }));
    })();
  }, []);

  useEffect(() => {
    if (!form.clienteId) return;
    (async () => {
      const [emp, srv, ref] = await Promise.all([
        empleadosApi.list({ clienteId: form.clienteId }),
        serviciosApi.list(),
        refaccionesApi.list(),
      ]);
      setEmpleados(emp);
      setServicios(srv);
      setRefacciones(ref);
    })();
  }, [form.clienteId]);

  const totales = useMemo(() => {
    let subtotal = 0;
    for (const it of form.items) {
      const bruto = Number(it.precioUnitario) * Number(it.cantidad || 1);
      const conDesc = bruto - Number(it.descuento || 0);
      subtotal += conDesc > 0 ? conDesc : 0;
    }
    subtotal = Math.round(subtotal * 100) / 100;
    const iva = Math.round(subtotal * 16) / 100;
    const total = Math.round((subtotal + iva) * 100) / 100;
    return { subtotal, iva, total };
  }, [form.items]);

  const addItem = () => {
    const today = new Date().toISOString().slice(0, 10);
    setForm(f => ({
      ...f,
      items: [...f.items, {
        id: crypto.randomUUID(),
        fecha: today,
        servicioId: null,
        descripcion: '',
        precioUnitario: 0,
        descuento: 0,
        cantidad: 1,
        empleadoId: empleados[0]?.id || null,
        guardarEnCatalogo: false,
        refacciones: [],
        evidencia: null,
      }],
    }));
  };

  const updateItem = (idx, patch) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], ...patch };
      return { ...f, items };
    });
  };

  const removeItem = (idx) => {
    setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        clienteId: form.clienteId,
        fechaInicio: new Date(form.fechaInicio),
        fechaFin: new Date(form.fechaFin),
        descripcionGeneral: form.descripcionGeneral,
        estatus: form.estatus,
        items: form.items.map(it => ({
          fecha: new Date(it.fecha),
          servicioId: it.servicioId || null,
          descripcionLibre: it.descripcion,
          precioUnitario: Number(it.precioUnitario),
          descuento: Number(it.descuento || 0),
          cantidad: Number(it.cantidad || 1),
          empleadoId: it.empleadoId || null,
          guardarEnCatalogo: !!it.guardarEnCatalogo,
          refacciones: it.refacciones || [],
          evidencias: it.evidencia ? [it.evidencia] : [],
        })),
      };
      const rel = await relacionesApi.create(form.clienteId, payload);
      setSuccess(`Relación #${rel.id} creada · ${rel.periodoLabel} · Total: $${rel.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`);
      setTimeout(() => navigate('/relaciones'), 1500);
    } catch (e) {
      const data = e?.response?.data;
      console.error('[Guardar Relación] fallo', e?.response?.status, data || e?.message);
      let msg =
        data?.title
        || data?.error
        || (data?.errors && Object.values(data.errors).flat().join(' · '))
        || e?.message
        || 'Error al guardar';
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  const capturaEvidencia = async (idx) => {
    if (!navigator.geolocation) { alert('Geolocalización no disponible'); return; }
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const evidencia = {
          tipoEvidencia: 'gps',
          latitud: pos.coords.latitude,
          longitud: pos.coords.longitude,
          nota: `GPS: ${pos.coords.latitude.toFixed(5)}, ${pos.coords.longitude.toFixed(5)} (precisión: ${pos.coords.accuracy.toFixed(0)}m)`,
        };
        updateItem(idx, { evidencia });
      },
      (err) => alert('No se pudo obtener GPS: ' + err.message)
    );
  };

  const clienteActual = clientes.find(c => c.id === form.clienteId);

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold text-white">Nueva Relación de Soporte</h1>
          <p className="text-sm text-muted">Wizard de captura · {clienteActual?.nombreComercial || '—'}</p>
        </div>
        <div className="sm:text-right text-sm">
          <div className="text-muted text-xs">TOTAL ESTIMADO</div>
          <div className="text-2xl font-bold text-emerald-400">${totales.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <StepBadge n={1} active={step >= 1} label="Periodo" />
        <ChevronRight size={16} className="text-muted" />
        <StepBadge n={2} active={step >= 2} label="Servicios" />
        <ChevronRight size={16} className="text-muted" />
        <StepBadge n={3} active={step >= 3} label="Refacciones" />
        <ChevronRight size={16} className="text-muted" />
        <StepBadge n={4} active={step >= 4} label="Revisar" />
      </div>

      {error && <Alert variant="error">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {step === 1 && (
        <div className="bg-panel border border-border rounded-xl p-6 space-y-4">
          <h2 className="font-semibold text-white text-lg">Paso 1 · Periodo y cliente</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-semibold text-slate-300 mb-1.5">CLIENTE</label>
              <select value={form.clienteId || ''} onChange={(e) => setForm({ ...form, clienteId: Number(e.target.value) })} className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white">
                {clientes.map(c => <option key={c.id} value={c.id}>{c.nombreComercial}</option>)}
              </select>
            </div>
            <FieldDate label="Fecha inicio" value={form.fechaInicio} onChange={(v) => setForm({ ...form, fechaInicio: v })} />
            <FieldDate label="Fecha fin" value={form.fechaFin} onChange={(v) => setForm({ ...form, fechaFin: v })} />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1.5">INTRODUCCIÓN / DESCRIPCIÓN GENERAL</label>
            <textarea
              rows={3}
              value={form.descripcionGeneral}
              onChange={(e) => setForm({ ...form, descripcionGeneral: e.target.value })}
              className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white focus:outline-none focus:border-cyan-500"
            />
            <p className="text-xs text-muted mt-1">Puedes usar HTML básico como &lt;b&gt; para negrita.</p>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-white text-lg">Paso 2 · Servicios ({form.items.length})</h2>
            <button onClick={addItem} className="flex items-center gap-2 px-3 py-1.5 bg-cyan-500 hover:bg-cyan-600 text-white text-xs font-semibold rounded-md">
              <Plus size={14} /> Agregar servicio
            </button>
          </div>

          {form.items.length === 0 ? (
            <div className="bg-panel border border-border border-dashed rounded-xl p-10 text-center">
              <FileText className="mx-auto text-muted mb-3" size={32} />
              <p className="text-muted text-sm">Aún no has agregado servicios. Haz clic en "Agregar servicio".</p>
            </div>
          ) : (
            form.items.map((it, idx) => (
              <ItemCard
                key={it.id}
                item={it}
                idx={idx}
                servicios={servicios}
                empleados={empleados}
                onChange={(patch) => updateItem(idx, patch)}
                onRemove={() => removeItem(idx)}
                onGps={() => capturaEvidencia(idx)}
              />
            ))
          )}
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-white text-lg">Paso 3 · Refacciones consumidas (opcional)</h2>
          <p className="text-sm text-muted">Asocia refacciones a cada servicio si fueron utilizadas. Puedes dejarlo vacío.</p>
          {form.items.map((it, idx) => (
            <RefaccionesCard
              key={it.id}
              item={it}
              refacciones={refacciones}
              idx={idx}
              onChange={(refs) => updateItem(idx, { refacciones: refs })}
            />
          ))}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-3">
          <h2 className="font-semibold text-white text-lg">Paso 4 · Revisar y guardar</h2>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 bg-panel border border-border rounded-xl p-5 space-y-2">
              <h3 className="font-semibold text-white">Resumen de servicios</h3>
              <div className="text-xs text-muted">{form.items.length} servicio(s) · {form.items.reduce((a, i) => a + Number(i.cantidad || 1), 0)} unidades</div>
              <div className="max-h-64 overflow-y-auto space-y-1 mt-2">
                {form.items.map((it, i) => (
                  <div key={i} className="text-xs text-slate-300 border-l-2 border-cyan-500 pl-2">
                    <span className="text-muted">{i + 1}.</span> {it.descripcion || '(sin descripción)'}
                    <span className="text-cyan-400 ml-2">${(Number(it.precioUnitario) * Number(it.cantidad || 1) - Number(it.descuento || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-panel border border-border rounded-xl p-5 space-y-3">
              <h3 className="font-semibold text-white">Totales</h3>
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">Subtotal</span>
                <span className="font-mono">${totales.subtotal.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-300">IVA (16%)</span>
                <span className="font-mono">${totales.iva.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
                <span className="text-white">Total</span>
                <span className="text-emerald-400 font-mono">${totales.total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>

          <div className="bg-panel border border-border rounded-xl p-5">
            <h3 className="font-semibold text-white mb-3">Generar PDF al guardar</h3>
            <p className="text-sm text-muted">Al hacer clic en "Guardar relación", se generará el PDF idéntico al formato estándar y quedará disponible en la lista de relaciones para descarga inmediata.</p>
          </div>
        </div>
      )}

      <div className="flex justify-between pt-4">
        <button onClick={() => navigate('/relaciones')} className="px-4 py-2 rounded border border-border text-slate-300 hover:bg-panel-2 text-sm">Cancelar</button>
        <div className="flex gap-2">
          {step > 1 && (
            <button onClick={() => setStep(s => s - 1)} className="flex items-center gap-1 px-4 py-2 rounded border border-border text-slate-300 hover:bg-panel-2 text-sm">
              <ChevronLeft size={14} /> Atrás
            </button>
          )}
          {step < 4 && (
            <button onClick={() => setStep(s => s + 1)} className="flex items-center gap-1 px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold">
              Siguiente <ChevronRight size={14} />
            </button>
          )}
          {step === 4 && (
            <button onClick={handleSubmit} disabled={saving || form.items.length === 0} className="flex items-center gap-2 px-5 py-2 rounded bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white text-sm font-bold disabled:opacity-50">
              {saving ? <Spinner size={14} /> : <Save size={14} />}
              {saving ? 'Guardando...' : 'Guardar Relación'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepBadge({ n, active, label }) {
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${active ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-panel-2 text-muted border border-border'}`}>
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] ${active ? 'bg-cyan-500 text-white' : 'bg-border text-slate-400'}`}>{active ? <CheckCircle2 size={12} /> : n}</span>
      {label}
    </div>
  );
}

function FieldDate({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-300 mb-1.5">{label}</label>
      <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-panel-2 border border-border rounded-md text-sm text-white focus:outline-none focus:border-cyan-500" />
    </div>
  );
}

function ItemCard({ item, idx, servicios, empleados, onChange, onRemove, onGps }) {
  const [search, setSearch] = useState('');
  const filtered = servicios.filter(s => !search || s.nombre.toLowerCase().includes(search.toLowerCase()) || s.codigo.toLowerCase().includes(search.toLowerCase()));

  const onSelectServicio = (s) => {
    onChange({ servicioId: s.id, descripcion: item.descripcion || s.nombre, precioUnitario: s.precioBase });
    setSearch('');
  };

  return (
    <div className="bg-panel border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-bold text-muted tracking-wider">SERVICIO #{idx + 1}</h3>
        <button onClick={onRemove} className="p-1 text-slate-400 hover:text-rose-400">
          <Trash2 size={14} />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-3">
          <label className="block text-[10px] font-bold text-slate-400 tracking-wider mb-1">FECHA</label>
          <input type="date" value={item.fecha} onChange={(e) => onChange({ fecha: e.target.value })} className="w-full px-2 py-1.5 bg-panel-2 border border-border rounded-md text-sm text-white" />
        </div>
        <div className="md:col-span-5">
          <label className="block text-[10px] font-bold text-slate-400 tracking-wider mb-1">SERVICIO / DESCRIPCIÓN</label>
          <div className="relative">
            <input
              type="text"
              value={item.descripcion}
              onChange={(e) => onChange({ descripcion: e.target.value, servicioId: null })}
              placeholder="Escribir descripción o buscar servicio..."
              className="w-full px-2 py-1.5 pr-8 bg-panel-2 border border-border rounded-md text-sm text-white focus:outline-none focus:border-cyan-500"
            />
            <Search size={12} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted" />
          </div>
          {servicios.length > 0 && (
            <details className="mt-1">
              <summary className="text-[10px] text-cyan-400 cursor-pointer hover:text-cyan-300">Ver catálogo ({servicios.length})</summary>
              <div className="mt-1 max-h-40 overflow-y-auto bg-panel-2 border border-border rounded-md p-1">
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtrar..." className="w-full px-2 py-1 mb-1 bg-bg border border-border rounded text-xs text-white" />
                {filtered.slice(0, 10).map(s => (
                  <button key={s.id} onClick={() => onSelectServicio(s)} className="block w-full text-left px-2 py-1 hover:bg-cyan-500/10 rounded text-xs">
                    <span className="font-mono text-cyan-400">{s.codigo}</span> {s.nombre} <span className="text-emerald-400">${Number(s.precioBase).toLocaleString('es-MX')}</span>
                  </button>
                ))}
              </div>
            </details>
          )}
        </div>
        <div className="md:col-span-2">
          <label className="block text-[10px] font-bold text-slate-400 tracking-wider mb-1">EMPLEADO</label>
          <select value={item.empleadoId || ''} onChange={(e) => onChange({ empleadoId: Number(e.target.value) || null })} className="w-full px-2 py-1.5 bg-panel-2 border border-border rounded-md text-sm text-white">
            <option value="">—</option>
            {empleados.map(e => <option key={e.id} value={e.id}>{e.nombreCompleto}</option>)}
          </select>
        </div>
        <div className="md:col-span-1">
          <label className="block text-[10px] font-bold text-slate-400 tracking-wider mb-1">CANT</label>
          <input type="number" min={1} step="0.5" value={item.cantidad} onChange={(e) => onChange({ cantidad: Number(e.target.value) })} className="w-full px-2 py-1.5 bg-panel-2 border border-border rounded-md text-sm text-white" />
        </div>
        <div className="md:col-span-1">
          <label className="block text-[10px] font-bold text-slate-400 tracking-wider mb-1">PRECIO</label>
          <input type="number" min={0} step="0.01" value={item.precioUnitario} onChange={(e) => onChange({ precioUnitario: Number(e.target.value) })} className="w-full px-2 py-1.5 bg-panel-2 border border-border rounded-md text-sm text-white" />
        </div>
      </div>

      <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
        <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
          <input type="checkbox" checked={item.guardarEnCatalogo} onChange={(e) => onChange({ guardarEnCatalogo: e.target.checked })} className="rounded border-border bg-panel-2 text-cyan-500 focus:ring-cyan-500" />
          Guardar descripción en catálogo
        </label>
        <button onClick={onGps} className="flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300" title="Capturar ubicación GPS del servicio">
          <MapPin size={12} /> {item.evidencia ? `GPS: ${item.evidencia.latitud?.toFixed(3)}, ${item.evidencia.longitud?.toFixed(3)}` : 'Capturar GPS'}
        </button>
      </div>
    </div>
  );
}

function RefaccionesCard({ item, refacciones, idx, onChange }) {
  const addRef = () => {
    onChange([...(item.refacciones || []), { refaccionId: refacciones[0]?.id, cantidad: 1, precioUnitario: refacciones[0]?.costo || 0 }]);
  };
  const updateRef = (i, patch) => {
    const arr = [...(item.refacciones || [])];
    arr[i] = { ...arr[i], ...patch };
    onChange(arr);
  };
  const removeRef = (i) => onChange(item.refacciones.filter((_, ix) => ix !== i));

  return (
    <div className="bg-panel border border-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <span className="text-xs font-mono text-muted">#{idx + 1}</span>
          <span className="text-sm text-white ml-2">{item.descripcion || '(sin descripción)'}</span>
        </div>
        <button onClick={addRef} className="text-xs text-cyan-400 hover:text-cyan-300">+ Agregar refacción</button>
      </div>
      {(item.refacciones || []).map((r, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 mt-2 items-center">
          <select value={r.refaccionId} onChange={(e) => { const rid = Number(e.target.value); const ref = refacciones.find(x => x.id === rid); updateRef(i, { refaccionId: rid, precioUnitario: ref?.costo || 0 }); }} className="col-span-6 px-2 py-1.5 bg-panel-2 border border-border rounded text-xs text-white">
            {refacciones.map(rf => <option key={rf.id} value={rf.id}>{rf.codigo} - {rf.nombre}</option>)}
          </select>
          <input type="number" value={r.cantidad} onChange={(e) => updateRef(i, { cantidad: Number(e.target.value) })} className="col-span-2 px-2 py-1.5 bg-panel-2 border border-border rounded text-xs text-white" placeholder="Cant" />
          <input type="number" value={r.precioUnitario} onChange={(e) => updateRef(i, { precioUnitario: Number(e.target.value) })} className="col-span-3 px-2 py-1.5 bg-panel-2 border border-border rounded text-xs text-white" placeholder="Precio" />
          <button onClick={() => removeRef(i)} className="col-span-1 text-rose-400 hover:text-rose-300 justify-self-center"><Trash2 size={12} /></button>
        </div>
      ))}
    </div>
  );
}
